import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  TableSortLabel,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { createMyOvertime, updateMyOvertime, deleteMyOvertime } from '../../services/overtimeAPIs';
import { getOvertimeByEmployeeId } from '../../services/overtimeAPI';
import { getMyProfile } from '../../services/EmployeeService';
import PageHeader from '../../components/headers/Header';

const MyOvertime = () => {
  const [overtimes, setOvertimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState({ date: '', hours: '', description: '' });
  const [editingId, setEditingId] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('date');

  useEffect(() => {
    fetchOvertimes();
  }, []);

  const fetchOvertimes = async () => {
    try {
      setLoading(true);
      const profile = await getMyProfile();
      setUserProfile(profile);
      
      const empId = profile?.id || profile?.employeeId;
      if (empId) {
        const data = await getOvertimeByEmployeeId(empId);
        setOvertimes(data || []);
      }
    } catch (error) {
      console.error('Failed to fetch overtimes:', error);
      showSnackbar('Failed to load overtimes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (err) {
      return dateString;
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFormData({ date: '', hours: '', description: '' });
    setEditingId(null);
  };

  const handleOpenModal = (overtime = null) => {
    if (overtime) {
      setFormData({
        date: overtime.date || '',
        hours: overtime.hours || '',
        description: overtime.description || ''
      });
      setEditingId(overtime.id);
    } else {
      setFormData({ date: '', hours: '', description: '' });
      setEditingId(null);
    }
    setOpenModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.date || !formData.hours || !formData.description) {
      showSnackbar('Please fill all fields', 'warning');
      return;
    }

    // Check for max 1 overtime per day (only for new creations)
    if (!editingId) {
      const existingForDate = overtimes.find(o => o.date === formData.date);
      if (existingForDate) {
        showSnackbar('You can only submit one overtime per day.', 'error');
        return;
      }
    }

    try {
      if (editingId) {
        await updateMyOvertime(editingId, {
          date: formData.date,
          hours: parseFloat(formData.hours),
          description: formData.description,
          confirmed: null
        });
        showSnackbar('Overtime updated successfully');
      } else {
        const payload = {
          employeeId: userProfile?.id || userProfile?.employeeId,
          date: formData.date,
          hours: parseFloat(formData.hours),
          description: formData.description,
          confirmed: null
        };
        await createMyOvertime(payload);
        showSnackbar('Overtime submitted successfully');
      }
      handleCloseModal();
      fetchOvertimes();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Action failed', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this overtime request?')) {
      try {
        await deleteMyOvertime(id);
        showSnackbar('Overtime deleted');
        fetchOvertimes();
      } catch (error) {
        showSnackbar('Failed to delete overtime', 'error');
      }
    }
  };

  const getStatusChip = (confirmed) => {
    if (confirmed === true) return <Chip label="Approved" color="success" size="small" />;
    if (confirmed === false) return <Chip label="Rejected" color="error" size="small" />;
    return <Chip label="Pending" color="warning" size="small" />;
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedOvertimes = [...overtimes].sort((a, b) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];
    
    if (orderBy === 'date') {
      const timeA = aVal ? new Date(aVal).getTime() : 0;
      const timeB = bVal ? new Date(bVal).getTime() : 0;
      aVal = isNaN(timeA) ? 0 : timeA;
      bVal = isNaN(timeB) ? 0 : timeB;
    } else if (orderBy === 'confirmed') {
      // Map status for sorting: null -> 0, true -> 1, false -> -1
      aVal = aVal === true ? 1 : aVal === false ? -1 : 0;
      bVal = bVal === true ? 1 : bVal === false ? -1 : 0;
    }
    
    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader title="My Overtime" subtitle="Manage your overtime requests" />
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => handleOpenModal()}>
          Submit Overtime
        </Button>
      </Box>

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={orderBy === 'date'}
                        direction={orderBy === 'date' ? order : 'asc'}
                        onClick={() => handleRequestSort('date')}
                      >
                        Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={orderBy === 'hours'}
                        direction={orderBy === 'hours' ? order : 'asc'}
                        onClick={() => handleRequestSort('hours')}
                      >
                        Hours
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={orderBy === 'confirmed'}
                        direction={orderBy === 'confirmed' ? order : 'asc'}
                        onClick={() => handleRequestSort('confirmed')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedOvertimes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">No overtime requests found.</TableCell>
                    </TableRow>
                  ) : (
                    sortedOvertimes.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{formatDate(row.date)}</TableCell>
                        <TableCell>{row.hours}</TableCell>
                        <TableCell>{row.description}</TableCell>
                        <TableCell>{getStatusChip(row.confirmed)}</TableCell>
                        <TableCell align="right">
                          {/* Only allow edit/delete if not confirmed (true) */}
                          {row.confirmed !== true && (
                            <>
                              <IconButton color="primary" onClick={() => handleOpenModal(row)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton color="error" onClick={() => handleDelete(row.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog 
        open={openModal} 
        onClose={handleCloseModal} 
        maxWidth="sm" 
        fullWidth
        disableRestoreFocus
        disableEnforceFocus
      >
        <DialogTitle>{editingId ? 'Edit Overtime' : 'Submit Overtime'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="date"
            label="Date"
            InputLabelProps={{ shrink: true }}
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            type="number"
            inputProps={{ step: "0.5", min: "0.5" }}
            label="Hours"
            value={formData.hours}
            onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>Submit</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MyOvertime;
